import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  Search,
  Download,
  Delete,
  Visibility,
  CloudUpload,
  InsertDriveFile,
  Image,
  PictureAsPdf,
  Link as LinkIcon,
  UploadFile,
} from '@mui/icons-material';
import { useNotification } from '../contexts/NotificationContext';

interface Document {
  id: number;
  filename: string;
  original_name: string;
  file_size: number;
  mime_type: string;
  related_to?: 'customer' | 'lead' | 'project' | 'Invoice';
  related_id?: number;
  related_name?: string;
  uploaded_by_name: string;
  created_at: string;
  type: 'file' | 'link';
  url?: string;
}

export default function Documents() {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [relatedFilter, setRelatedFilter] = useState<string>('all');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadType, setUploadType] = useState<'file' | 'link'>('file');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkName, setLinkName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [documentName, setDocumentName] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(null);
  const [overallStats, setOverallStats] = useState({
    totalFiles: 0,
    totalSize: 0,
    imageFiles: 0,
    pdfFiles: 0
  });
  const { showSuccess, showError } = useNotification();

  // Load documents and stats on component mount and when page changes
  React.useEffect(() => {
    loadDocuments();
    loadOverallStats();
  }, [page, rowsPerPage]);

  const loadOverallStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/documents/stats', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const stats = await response.json();
        setOverallStats(stats);
      }
    } catch (error) {
      console.error('Error loading document stats:', error);
    }
  };

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/documents?page=${page + 1}&limit=${rowsPerPage}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
        setTotalDocuments(data.total || 0);
      } else {
        showError('Failed to load documents');
      }
    } catch (error) {
      console.error('Error loading documents:', error);
      showError('Error loading documents');
    } finally {
      setLoading(false);
    }
  };

  const uploadDocument = async (file: File, name?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (name) {
      formData.append('document_name', name);
    }

    try {
      setUploading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/documents/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        showSuccess('Document uploaded successfully');
        loadDocuments(); // Reload documents list
        loadOverallStats(); // Reload stats
        return true;
      } else {
        const errorData = await response.json();
        showError(errorData.message || 'Failed to upload document');
        return false;
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      showError('Error uploading document');
      return false;
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleFileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const addExternalLink = async (name: string, url: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/documents/link', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, url }),
      });

      if (response.ok) {
        showSuccess('External link added successfully');
        loadDocuments(); // Reload documents list
        loadOverallStats(); // Reload stats
        return true;
      } else {
        const errorData = await response.json();
        showError(errorData.message || 'Failed to add external link');
        return false;
      }
    } catch (error) {
      console.error('Error adding external link:', error);
      showError('Error adding external link');
      return false;
    }
  };

  const filteredDocuments = documents.filter(document => {
    const matchesSearch = 
      document.original_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (document.related_name && document.related_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      document.uploaded_by_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = typeFilter === 'all' || document.mime_type.startsWith(typeFilter);
    const matchesRelated = relatedFilter === 'all' || 
                          (relatedFilter === 'standalone' && !document.related_to) ||
                          document.related_to === relatedFilter;
    
    return matchesSearch && matchesType && matchesRelated;
  });

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    if (!bytes || isNaN(bytes) || bytes < 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (document: Document) => {
    if (document.type === 'link') return <LinkIcon color="info" />;
    if (document.mime_type.startsWith('image/')) return <Image color="primary" />;
    if (document.mime_type === 'application/pdf') return <PictureAsPdf color="error" />;
    return <InsertDriveFile color="action" />;
  };

  const getRelatedColor = (type: string) => {
    const colors: { [key: string]: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' } = {
      customer: 'primary',
      lead: 'secondary',
      project: 'success',
      invoice: 'warning',
    };
    return colors[type] || 'default';
  };

  const getTotalStats = () => {
    // Return overall stats for all documents in the system
    return overallStats;
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteClick = (doc: Document) => {
    setDocumentToDelete(doc);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/documents/${documentToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        showSuccess('Document deleted successfully');
        loadDocuments(); // Reload documents list
        loadOverallStats(); // Reload stats
      } else {
        const errorData = await response.json();
        showError(errorData.message || 'Failed to delete document');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      showError('Error deleting document');
    } finally {
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
    }
  };

  const handleViewDocument = (doc: Document) => {
    if (doc.type === 'link' && doc.url) {
      window.open(doc.url, '_blank');
    } else {
      // For files, trigger download (same as download button)
      const link = document.createElement('a');
      link.href = `/api/documents/download/${doc.id}`;
      link.download = doc.original_name || doc.filename || 'download';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDownloadDocument = (doc: Document) => {
    if (doc.type === 'link' && doc.url) {
      window.open(doc.url, '_blank');
    } else {
      // For files, trigger download using a hidden link
      const link = document.createElement('a');
      link.href = `/api/documents/download/${doc.id}`;
      link.download = doc.original_name || doc.filename || 'download';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const stats = getTotalStats();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setUploadDialogOpen(true)}
        >
          Add Document
        </Button>
      </Box>

      {/* Summary Cards */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' }, 
        flexWrap: 'wrap',
        gap: 3, 
        mb: 3 
      }}>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Files
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="primary">
                {stats.totalFiles}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Storage Used
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="secondary">
                {formatFileSize(stats.totalSize)}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Images
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {stats.imageFiles}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                PDFs
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="error.main">
                {stats.pdfFiles}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' }, 
            gap: 2, 
            alignItems: { md: 'center' } 
          }}>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 40%' } }}>
              <TextField
                fullWidth
                placeholder="Search documents, projects, or leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 25%' } }}>
              <FormControl fullWidth>
                <InputLabel>File Type</InputLabel>
                <Select
                  value={typeFilter}
                  label="File Type"
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="image">Images</MenuItem>
                  <MenuItem value="application">PDFs & Documents</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 25%' } }}>
              <FormControl fullWidth>
                <InputLabel>Related To</InputLabel>
                <Select
                  value={relatedFilter}
                  label="Related To"
                  onChange={(e) => setRelatedFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="standalone">Standalone</MenuItem>
                  <MenuItem value="customer">Customers</MenuItem>
                  <MenuItem value="lead">Leads</MenuItem>
                  <MenuItem value="project">Projects</MenuItem>
                  <MenuItem value="invoice">Invoices</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 10%' } }}>
              <Typography variant="body2" color="textSecondary">
                {filteredDocuments.length} files found
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>File</TableCell>
              <TableCell>Related To</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>Uploaded By</TableCell>
              <TableCell>Upload Date</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDocuments.map((document) => (
              <TableRow 
                key={document.id}
                sx={{ 
                  '&:hover': { 
                    backgroundColor: 'action.hover',
                    cursor: 'pointer',
                    transform: 'translateY(-1px)',
                    boxShadow: 1,
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ mr: 2 }}>
                      {getFileIcon(document)}
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {document.original_name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {document.mime_type}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    {document.related_to ? (
                      <>
                        <Chip
                          label={document.related_to}
                          color={getRelatedColor(document.related_to)}
                          size="small"
                          variant="outlined"
                        />
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {document.related_name}
                        </Typography>
                      </>
                    ) : (
                      <Chip
                        label="Standalone"
                        color="default"
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  {document.type === 'link' ? 'External Link' : formatFileSize(document.file_size)}
                </TableCell>
                <TableCell>{document.uploaded_by_name}</TableCell>
                <TableCell>
                  {new Date(document.created_at).toLocaleDateString('en-GB')}
                </TableCell>
                <TableCell align="right">
                  <IconButton 
                    size="small" 
                    title="View Document"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDocument(document);
                    }}
                  >
                    <Visibility />
                  </IconButton>
                  {document.type === 'file' && (
                    <IconButton 
                      size="small" 
                      title="Download Document"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadDocument(document);
                      }}
                    >
                      <Download />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    title="Delete Document"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(document);
                    }}
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={totalDocuments}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Upload/Add Document Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Document</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, mb: 3 }}>
            <ToggleButtonGroup
              value={uploadType}
              exclusive
              onChange={(e, newType) => {
                if (newType) {
                  setUploadType(newType);
                  setSelectedFile(null);
                  setLinkUrl('');
                  setLinkName('');
                  setDocumentName('');
                }
              }}
              fullWidth
            >
              <ToggleButton value="file">
                <UploadFile sx={{ mr: 1 }} />
                Upload File
              </ToggleButton>
              <ToggleButton value="link">
                <LinkIcon sx={{ mr: 1 }} />
                Add External Link
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {uploadType === 'file' ? (
            <Box>
              <TextField
                fullWidth
                label="Document Name"
                value={documentName}
                onChange={(e) => setDocumentName(e.target.value)}
                margin="normal"
                placeholder="e.g., Order Form, Kitchen Quote"
                helperText="Optional: Give this document a friendly name"
              />
              <input
                type="file"
                id="file-upload"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
                accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx,.txt"
              />
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: selectedFile ? 'success.main' : 'primary.main',
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  mb: 3,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: 'primary.dark',
                    backgroundColor: 'action.hover',
                  },
                }}
                onClick={() => document.getElementById('file-upload')?.click()}
                onDrop={handleFileDrop}
                onDragOver={handleDragOver}
              >
                <CloudUpload sx={{ fontSize: 48, color: selectedFile ? 'success.main' : 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {selectedFile ? selectedFile.name : 'Drop files here or click to browse'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {selectedFile 
                    ? `Size: ${(selectedFile.size / 1024 / 1024).toFixed(2)} MB`
                    : 'Supported formats: PDF, JPG, PNG, DOC, DOCX (Max 10MB)'
                  }
                </Typography>
                {!selectedFile && (
                  <Button variant="outlined" sx={{ mt: 2 }}>
                    Choose Files
                  </Button>
                )}
                {selectedFile && (
                  <Button 
                    variant="outlined" 
                    sx={{ mt: 2, mr: 1 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                    }}
                  >
                    Remove
                  </Button>
                )}
              </Box>
            </Box>
          ) : (
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Document Name"
                value={linkName}
                onChange={(e) => setLinkName(e.target.value)}
                margin="normal"
                placeholder="e.g., Kitchen Design Brochure"
              />
              <TextField
                fullWidth
                label="External Link URL"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                margin="normal"
                placeholder="https://example.com/brochure.pdf"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setUploadDialogOpen(false);
              setUploadType('file');
              setLinkUrl('');
              setLinkName('');
              setSelectedFile(null);
              setDocumentName('');
            }}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            disabled={uploading || (uploadType === 'file' && !selectedFile) || (uploadType === 'link' && (!linkName || !linkUrl))}
            onClick={async () => {
              if (uploadType === 'link') {
                if (!linkName || !linkUrl) {
                  showError('Please provide both document name and URL');
                  return;
                }
                const success = await addExternalLink(linkName, linkUrl);
                if (success) {
                  setUploadDialogOpen(false);
                  setUploadType('file');
                  setLinkUrl('');
                  setLinkName('');
                  setSelectedFile(null);
                  setDocumentName('');
                }
              } else {
                if (!selectedFile) {
                  showError('Please select a file to upload');
                  return;
                }
                const success = await uploadDocument(selectedFile, documentName || undefined);
                if (success) {
                  setUploadDialogOpen(false);
                  setUploadType('file');
                  setLinkUrl('');
                  setLinkName('');
                  setSelectedFile(null);
                  setDocumentName('');
                }
              }
            }}
          >
            {uploading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            {uploadType === 'file' ? (uploading ? 'Uploading...' : 'Upload') : 'Add Link'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>
            Do you really want to delete this document?
          </Typography>
          {documentToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" gutterBottom>
                <strong>File Name:</strong> {documentToDelete.original_name}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Type:</strong> {documentToDelete.type === 'link' ? 'External Link' : documentToDelete.mime_type}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Size:</strong> {documentToDelete.type === 'link' ? 'N/A' : formatFileSize(documentToDelete.file_size)}
              </Typography>
              <Typography variant="subtitle2">
                <strong>Uploaded By:</strong> {documentToDelete.uploaded_by_name}
              </Typography>
            </Box>
          )}
          <Typography color="error" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setDocumentToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}